import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import EastIcon from '@mui/icons-material/East';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ml from '../../images/ml.jpg';
import cyber from '../../images/cyber.jpg';
import './CourseDetails.css';
import bgImage2 from "../../images/glow.png";


// Course data
const courseData: { [key: string]: any } = {
  'machine-learning-ai': {
    title: 'Machine Learning and AI',
    image: ml,
    duration: '3 months',
    description: 'Build a Portfolio of Machine Learning Projects with Python, SVM, Regression, Unsupervised Machine Learning & More!',
    about: 'Our Machine Learning and AI course is designed to provide you with comprehensive knowledge and hands-on experience in the field of artificial intelligence. You will learn to build real-world AI projects, understand various machine learning algorithms, and develop the skills needed to excel in this rapidly growing field.',
    keyFeatures: {
      courseFeatures: [
        'Python fundamentals & tools',
        'Machine Learning Algorithms',
        'Build AI projects',
        'Real-world AI use cases',
        'Hands-on project experience',
        'Industry-relevant curriculum'
      ],
      sampleInternshipProjects: [
        'Image Classification System',
        'Natural Language Processing Chatbot',
        'Recommendation Engine',
        'Predictive Analytics Dashboard',
        'Computer Vision Application'
      ],
      interviewTraining: [
        'Technical interview preparation',
        'ML algorithm explanations',
        'Coding challenges practice',
        'System design for AI applications',
        'Portfolio project presentations',
        'Mock interviews with industry experts'
      ]
    },
    companies: [
      'Google',
      'Microsoft',
      'Amazon',
      'IBM',
      'Apple',
      'Meta',
      'Tesla',
      'NVIDIA'
    ],
    curriculum: [
      {
        module: 'Module 1: Python Fundamentals',
        topics: ['Python Basics', 'Data Structures', 'Object-Oriented Programming', 'Libraries: NumPy, Pandas, Matplotlib']
      },
      {
        module: 'Module 2: Machine Learning Basics',
        topics: ['Introduction to ML', 'Supervised Learning', 'Unsupervised Learning', 'Model Evaluation']
      },
      {
        module: 'Module 3: Advanced ML Algorithms',
        topics: ['SVM', 'Regression Models', 'Decision Trees', 'Random Forests', 'Neural Networks']
      },
      {
        module: 'Module 4: Deep Learning',
        topics: ['Introduction to Deep Learning', 'TensorFlow & Keras', 'CNN', 'RNN', 'LSTM']
      },
      {
        module: 'Module 5: AI Projects',
        topics: ['Project 1: Image Classification', 'Project 2: Natural Language Processing', 'Project 3: Recommendation System', 'Capstone Project']
      }
    ]
  },
  'ai-cybersecurity-guardian': {
    title: 'AI Cybersecurity Guardian',
    image: cyber,
    duration: '3 months',
    description: 'Master the Art of Cyber Security: Learn Essential Skills for Online Safety and Protect Your Digital World',
    about: 'The AI Cybersecurity Guardian course equips you with the knowledge and skills to protect digital assets in an increasingly connected world. Learn to identify threats, implement security measures, and use AI-powered tools to defend against cyber attacks.',
    keyFeatures: {
      courseFeatures: [
        'Security Governance, Network Security',
        'Cyber Attacks, Web Application Security, Malware',
        'CompTIA Security+ and CEH preparation',
        'AI-powered security tools',
        'Real-world security scenarios',
        'Hands-on lab exercises'
      ],
      sampleInternshipProjects: [
        'Vulnerability Assessment Tool',
        'Network Security Monitoring System',
        'AI-Powered Threat Detection System',
        'Security Incident Response Automation',
        'Penetration Testing Framework'
      ],
      interviewTraining: [
        'Cybersecurity interview preparation',
        'Security concepts and best practices',
        'Scenario-based problem solving',
        'Certification exam preparation',
        'Security tool demonstrations',
        'Mock interviews with security professionals'
      ]
    },
    companies: [
      'Cisco',
      'Palo Alto Networks',
      'Fortinet',
      'Check Point',
      'Symantec',
      'FireEye',
      'CrowdStrike',
      'Rapid7'
    ],
    curriculum: [
      {
        module: 'Module 1: Cybersecurity Fundamentals',
        topics: ['Introduction to Cybersecurity', 'Security Governance', 'Risk Management', 'Compliance & Regulations']
      },
      {
        module: 'Module 2: Network Security',
        topics: ['Network Architecture', 'Firewalls & IDS/IPS', 'VPN Technologies', 'Wireless Security']
      },
      {
        module: 'Module 3: Threats & Attacks',
        topics: ['Malware Analysis', 'Web Application Security', 'Social Engineering', 'DDoS Attacks']
      },
      {
        module: 'Module 4: AI in Cybersecurity',
        topics: ['AI-Powered Threat Detection', 'Machine Learning for Security', 'Automated Response Systems', 'Security Analytics']
      },
      {
        module: 'Module 5: Certification Preparation',
        topics: ['CompTIA Security+ Prep', 'CEH Exam Preparation', 'Hands-on Labs', 'Capstone Project']
      }
    ]
  },
  'vlsi-design-verification': {
    title: 'VLSI Design and Verification',
    image: cyber,
    duration: '3 months',
    description: 'Master the Art of VLSI Design and Verification: Learn Essential Skills for Chip Design and Verification',
    about: 'The VLSI Design and Verification course provides comprehensive training in Very Large Scale Integration (VLSI) design principles, verification methodologies, and industry-standard tools. Learn to design and verify complex digital circuits and systems-on-chip (SoC).',
    keyFeatures: {
      courseFeatures: [
        'Digital Design Fundamentals',
        'RTL Design & Synthesis',
        'Verification Methodologies',
        'Industry-standard tools (Cadence, Synopsys)',
        'FPGA Implementation',
        'ASIC Design Flow'
      ],
      sampleInternshipProjects: [
        'RTL Design of ALU Unit',
        'UVM Testbench Development',
        'FPGA Implementation Project',
        'Timing Closure and Optimization',
        'SoC Integration Project'
      ],
      interviewTraining: [
        'VLSI design interview preparation',
        'RTL coding challenges',
        'Verification methodology questions',
        'Timing analysis and constraints',
        'Tool-specific technical questions',
        'Mock interviews with VLSI engineers'
      ]
    },
    companies: [
      'Intel',
      'AMD',
      'Qualcomm',
      'NVIDIA',
      'Broadcom',
      'MediaTek',
      'ARM',
      'Xilinx'
    ],
    curriculum: [
      {
        module: 'Module 1: Digital Design Fundamentals',
        topics: ['Boolean Algebra', 'Combinational Logic', 'Sequential Logic', 'Finite State Machines']
      },
      {
        module: 'Module 2: RTL Design',
        topics: ['Verilog/VHDL Basics', 'RTL Coding Standards', 'Design for Synthesis', 'Clock Domain Crossing']
      },
      {
        module: 'Module 3: Verification Methodologies',
        topics: ['SystemVerilog', 'UVM Framework', 'Testbench Development', 'Coverage Analysis']
      },
      {
        module: 'Module 4: Physical Design',
        topics: ['Synthesis & Optimization', 'Place & Route', 'Timing Analysis', 'Power Analysis']
      },
      {
        module: 'Module 5: Advanced Topics',
        topics: ['SoC Design', 'FPGA Implementation', 'ASIC Design Flow', 'Capstone Project']
      }
    ]
  }
};

export const CourseDetails = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const isMobileScreen = useMediaQuery({ query: '(max-width: 900px)' });
  const [selectedModuleIndex, setSelectedModuleIndex] = useState(0);
  const backgroundRef = useRef<HTMLDivElement | null>(null);
  const heroRef = useRef<HTMLDivElement | null>(null);

  
  const course = courseId ? courseData[courseId] : null;


  useEffect(() => {
    if (heroRef.current && backgroundRef.current) {
      heroRef.current.style.background = 'none';
      backgroundRef.current.style.backgroundImage = `url(${bgImage2})`;
    }
    const handleScroll = () => {
      if (backgroundRef.current) {
        const maxScroll = 630;
        const maxScaleTransform = 1.6;
        const maxScaleTop = 1.68;
        let scale = window.scrollY / 900 + 1;
        let scale1 = Math.max(1.6, scale);
        let scaleTop = window.scrollY / 910 + 1;
        let scaleside = window.scrollY / 910 + 1;
          console.log(window.scrollY );
      
        // if (window.scrollY > maxScroll) {
        //   backgroundRef.current.style.transform = `scale(1.6, 1.6)`;
        //   // backgroundRef.current.style.left = '-5%';
        //   scaleTop = 1.68;
          
        //   // backgroundRef.current.style.top = ``;

        // }
        if (window.scrollY > maxScroll) { 
          scale1= maxScaleTransform;
   
          scaleside = maxScaleTop ;
          scaleTop = maxScaleTop;
   

        }
        // if (window.scrollY > 750) { 
        //   scale1= maxScaleTransform;
   
        //   // scaleside = -(scaleside - 1) ;
        //   backgroundRef.current.style.left = `${(26.47 * scaleside) + 36.47}%`;
   

        // }
      

        backgroundRef.current.style.transform = `scale(${scale1 }, ${scale1 })`;
        // backgroundRef.current.style.transformOrigin = 'bottom right';
        backgroundRef.current.style.bottom = `-${(scaleTop - 1) *100}%`;
        // backgroundRef.current.style.left = `${(-26.47 * scaleside) + 30.47}%`;
      }
    };
    const handleScrollMobile = () => {
      if (backgroundRef.current) {
        const maxScroll = 630;
        const maxScaleTransform = 1.6;
        const maxScaleTop = 1.68;
        let scale = window.scrollY / 900 + 1;
        let scale1 = Math.max(1.6, scale);
        let scaleTop = window.scrollY / 910 + 1;
        let scaleside = window.scrollY / 910 + 1;
          console.log(window.scrollY );
      
        // if (window.scrollY > maxScroll) {
        //   backgroundRef.current.style.transform = `scale(1.6, 1.6)`;
        //   // backgroundRef.current.style.left = '-5%';
        //   scaleTop = 1.68;
          
        //   // backgroundRef.current.style.top = ``;

        // }
        if (window.scrollY > maxScroll) { 
          scale1= maxScaleTransform;
   
          scaleside = maxScaleTop ;
          scaleTop = 1.8;
   

        }
        // if (window.scrollY > 750) { 
        //   scale1= maxScaleTransform;
   
        //   // scaleside = -(scaleside - 1) ;
        //   backgroundRef.current.style.left = `${(26.47 * scaleside) + 36.47}%`;
   

        // }
      

        // backgroundRef.current.style.transform = `scale(${scale1 }, ${scale1 })`;
        // backgroundRef.current.style.txransformOrigin = 'bottom right';
        // backgroundRef.current.style.top = `${(-22.05 * scaleTop) + 47}%`;
        backgroundRef.current.style.bottom = `-${(scaleTop - 1) *100}%`;
        // backgroundRef.current.style.left = `${(-30.47 * scaleside) + 26.47}%`;
      }
    };
    const updateScrollHandler = () => {
      const isMobile = window.innerWidth <= 900; // Adjust breakpoint as needed
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScrollMobile);
  
      if (isMobile) {
        window.addEventListener('scroll', handleScrollMobile);
      } else {
        window.addEventListener('scroll', handleScroll);
      }
    };
  
    // Set the initial scroll handler
    updateScrollHandler();
     // Update the scroll handler on resize
  window.addEventListener('resize', updateScrollHandler);
    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScrollMobile);
      window.removeEventListener('resize', updateScrollHandler);
    };
}, []);


  if (!course) {
    return (
      <div className="course-details-wrapper">
        <div className="course-not-found">
          <h1>Course not found</h1>
          <button onClick={() => navigate('/training')} className="back-button">
            <ArrowBackIcon /> Back to Training
          </button>
        </div>
      </div>
    );
  }

  const handleApplyNow = () => {
    const url = "https://forms.office.com/Pages/ResponsePage.aspx?id=tyjdRuz3r0e4Spk5XXjNRu6D1IpZpNpEhgSE6_5_gVZUMzBRUEJITVhLWFRXV0dNNUVPTFZMMEpFSyQlQCN0PWcu";
    window.open(url, "_blank");
  };

  const handleDownloadBrochure = () => {
    // In a real application, this would download the actual brochure PDF
    alert('Brochure download will be available soon!');
  };

  return (
    <div className="course-details-wrapper">
      {/* Header Section with Apply Now and Download Brochure */}
      <section className="course-header-section" ref={heroRef}>
      <div   className='glow_2'></div>

<div ref={backgroundRef} className="background_t">
<div   ref={backgroundRef}className='glow'></div>
</div>
        <button onClick={() => navigate('/training')} className="back-button">
          <ArrowBackIcon /> Back to Training
        </button>
        <div className="course-header-content">
          <div className="course-header-image">
            <img src={course.image} alt={course.title} />
          </div>
          <div className="course-header-info">
            <h1>{course.title}</h1>
            <p className="course-duration">Course Duration: {course.duration}</p>
            <p className="course-description">{course.description}</p>
            <div className="course-header-buttons">
              <button className="apply-now-button" onClick={handleApplyNow}>
                Apply Now
                <EastIcon className="arrow-icon" />
              </button>
              <button className="download-brochure-button" onClick={handleDownloadBrochure}>
                <DownloadIcon className="download-icon" />
                Download Brochure
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About The Course Section */}
      <section className="course-about-section">
        <h2>About The Course</h2>
        <p>{course.about}</p>
      </section>

      {/* Key Features Section */}
      <section className="course-features-section">
        <h2>Key Features</h2>
        <div className="features-container">
          {/* Course Features */}
          <div className="feature-category">
            <h3 className="feature-category-title">Course Features</h3>
            <div className="features-grid">
              {course.keyFeatures.courseFeatures.map((feature: string, index: number) => (
                <div key={index} className="feature-item">
                  <div className="feature-icon">✓</div>
                  <p>{feature}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Sample Internship Projects */}
          <div className="feature-category">
            <h3 className="feature-category-title">Sample Internship Projects</h3>
            <div className="features-grid">
              {course.keyFeatures.sampleInternshipProjects.map((feature: string, index: number) => (
                <div key={index} className="feature-item">
                  <div className="feature-icon">✓</div>
                  <p>{feature}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Interview Training */}
          <div className="feature-category">
            <h3 className="feature-category-title">Interview Training</h3>
            <div className="features-grid">
              {course.keyFeatures.interviewTraining.map((feature: string, index: number) => (
                <div key={index} className="feature-item">
                  <div className="feature-icon">✓</div>
                  <p>{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Companies You Can Apply Section */}
      <section className="course-companies-section">
        <h2>Companies You Can Apply</h2>
        <div className="companies-grid">
          {course.companies.map((company: string, index: number) => (
            <div key={index} className="company-item">
              {company}
            </div>
          ))}
        </div>
      </section>

      {/* Course Curriculum Section */}
      <section className="course-curriculum-section">
        <h2>Course Curriculum</h2>
        <div className="curriculum-container">
          <div className="curriculum-tabs">
            {course.curriculum.map((module: any, index: number) => (
              <button
                key={index}
                className={`curriculum-tab ${selectedModuleIndex === index ? 'active' : ''}`}
                onClick={() => setSelectedModuleIndex(index)}
              >
                {module.module}
              </button>
            ))}
          </div>
          <div className="curriculum-content">
            {course.curriculum[selectedModuleIndex] && (
              <div className="curriculum-module-content">
                <h3>{course.curriculum[selectedModuleIndex].module}</h3>
                <ul>
                  {course.curriculum[selectedModuleIndex].topics.map((topic: string, topicIndex: number) => (
                    <li key={topicIndex}>{topic}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

